import type { Session } from '@supabase/supabase-js';
import { friendlyApiError } from './api-errors';

export interface DashboardData {
  automation: {
    automation_paused: boolean;
    operation_mode: 'SUPERVISED' | 'AUTONOMOUS';
    daily_cycle_time: string;
    timezone: string;
  };
  accounts: Array<{
    platform: 'instagram' | 'facebook' | 'tiktok';
    status: string;
  }>;
  lastCycle: {
    id: string;
    status: string;
    started_at: string;
    finished_at: string | null;
  } | null;
  localAgent: {
    id: string;
    name: string;
    status: string;
    last_seen_at: string | null;
    version?: string;
    capabilities?: string[];
  } | null;
  scheduledToday: number;
  health?: {
    api: 'RESPONDING';
    supabase: 'READ_OK';
    r2BindingConfigured: boolean;
    queueBindingConfigured: boolean;
    mediaSigningConfigured: boolean;
  };
  intelligence: {
    id: string;
    as_of: string;
    observation_count: number;
    finding_count: number;
  } | null;
}

export interface IntelligenceData {
  run: {
    id: string;
    analysis_version: number;
    status: string;
    as_of: string;
    observation_count: number;
    finding_count: number;
    summary: Array<{
      dimension: string;
      subject: string;
      cohort: string;
      sampleSize: number;
      medianShareRate: number | null;
      medianViews: number | null;
    }>;
    insufficient: Array<{
      dimension: string;
      subject: string;
      sampleSize: number;
    }>;
  } | null;
  findings: Array<{
    id: string;
    kind: string;
    dimension: string;
    subject: string;
    title: string;
    recommendation: string;
    confidence: string;
    sample_size: number;
    evidence: Record<string, string | number>;
    status: string;
    created_at: string;
    expires_at: string;
  }>;
  observations: Array<{
    publication_id: string;
    cohort: string;
    age_hours: number;
    window_hours: number | null;
    snapshot_at: string;
    metrics: Record<string, number>;
    rates: Record<string, number>;
    attribution: string | null;
  }>;
  learnings: Array<{
    id: string;
    finding_id: string;
    title: string;
    recommendation: string;
    status: string;
    evidence: Record<string, unknown>;
    effective_at: string;
    expires_at: string;
  }>;
  adjustments: Array<{
    id: string;
    target_type: string;
    target_id: string;
    proposed_delta: number;
    before_value: number | null;
    after_value: number | null;
    status: string;
    applied_at: string | null;
  }>;
  settings: {
    minimum_sample: number;
    lookback_days: number;
    autonomous_idea_potential_delta: number;
  } | null;
}

export interface EditorialDna {
  user_id: string;
  positioning: string;
  identity_description: string;
  language_style: string;
  tone: string;
  preferred_vocabulary: string[];
  avoided_vocabulary: string[];
  pastoral_style: string;
  values: string[];
  priority_themes: string[];
  sensitive_themes: string[];
  prohibited_themes: string[];
  depth: string;
  message_structure: string;
  presentation_style: string;
  bible_usage: string;
  hook_types: string[];
  ctas: string[];
  visual_style: string;
  approved_examples: string[];
  unsuitable_examples: string[];
  additional_rules: string[];
  version: number;
}

export interface EditorialSettings {
  max_daily_items: number;
  windows: string[];
  min_quality: number;
}

export interface Pillar {
  id: string;
  name: string;
  description: string;
  weight: number;
  active: boolean;
  sort_order: number;
}

export interface EditorialSeries {
  id: string;
  name: string;
  description: string;
  status: 'ACTIVE' | 'PAUSED' | 'COMPLETED';
  sort_order: number;
  pillar_id: string | null;
  themes: string[];
  planned_count: number;
  published_count: number;
  start_date: string | null;
  end_date: string | null;
  notes: string;
}

export interface BibleReference {
  id: string;
  book: string;
  chapter: number;
  verse_start: number;
  verse_end: number | null;
  normalized_reference: string;
  passage_text: string | null;
  translation: string | null;
  source: string | null;
  text_license: string | null;
  context: string;
  interpretation: string;
  pastoral_application: string;
  experience_opinion: string;
  notes: string;
  provenance: string;
  human_reviewed: boolean;
}

export interface EditorialIdea {
  id: string;
  title: string;
  description: string;
  origin: 'MANUAL' | 'INTERNAL_RESEARCH' | 'PUBLIC_SOURCE';
  source_detail: string;
  category: string;
  pillar_id: string | null;
  series_id: string | null;
  audiences: string[];
  topic_key: string;
  originating_question: string;
  bible_reference_id: string | null;
  priority: number;
  potential: number;
  status: 'AVAILABLE' | 'PLANNED' | 'ARCHIVED';
  tags: string[];
  expires_at: string | null;
  notes: string;
  related_content_id: string | null;
  requires_biblical_foundation: boolean;
}

export interface EditorialRule {
  id: string;
  rule_key: string;
  description: string;
  enabled: boolean;
  severity: 'BLOCK' | 'WARN';
}

export interface EditorialPlan {
  id: string;
  plan_date: string;
  mode: 'SUPERVISED' | 'AUTONOMOUS';
  status: string;
  correlation_id: string;
}

export interface EditorialItem {
  id: string;
  plan_id: string;
  slot: number;
  planned_date: string;
  master_time: string;
  title: string;
  approach: string;
  hook: string;
  objective: string;
  audiences: string[];
  pillar_id: string | null;
  series_id: string | null;
  bible_reference_id: string | null;
  biblical_text_note: string;
  context_note: string;
  interpretation_note: string;
  application_note: string;
  opinion_note: string;
  structure: string;
  script: string;
  cta: string;
  intended_format: string;
  intended_platforms: Array<'instagram' | 'facebook' | 'tiktok'>;
  status: string;
  review_note: string;
  selection_reason: string;
  source_provenance: string;
  quality_score: number;
}

export interface EditorialBootstrap {
  dna: EditorialDna[];
  settings: EditorialSettings[];
  pillars: Pillar[];
  series: EditorialSeries[];
  references: BibleReference[];
  ideas: EditorialIdea[];
  rules: EditorialRule[];
  automation: DashboardData['automation'][];
}

export type MediaFormat = 'CARD' | 'CAROUSEL' | 'STORY' | 'COVER' | 'VIDEO';
export interface MediaTemplate {
  id: string;
  user_id: string | null;
  slug: string;
  name: string;
  category: MediaFormat;
  width: number;
  height: number;
  version: number;
  layout: {
    background: string;
    foreground: string;
    accent: string;
    variant: 'editorial' | 'minimal';
    safe: number;
    maxTitle: number;
  };
  active: boolean;
}
export interface MediaSpec {
  id: string;
  editorial_item_id: string | null;
  content_id: string;
  template_id: string;
  format: MediaFormat;
  status:
    'PENDING' | 'RENDERING' | 'VALIDATING' | 'READY' | 'FAILED' | 'PAUSED';
  spec: {
    title: string;
    script: string;
    caption: string;
    sourceAssetId: string | null;
  };
  job_id: string;
  correlation_id: string;
  last_error: { code?: string; message?: string } | null;
  created_at: string;
}
export interface MediaAsset {
  id: string;
  content_id: string | null;
  parent_asset_id: string | null;
  render_job_id: string | null;
  asset_type: string;
  media_role: 'ORIGINAL' | 'DERIVED' | 'FINAL' | 'CAPTION' | 'PREVIEW';
  mime_type: string;
  width: number | null;
  height: number | null;
  duration_ms: number | null;
  size_bytes: number;
  checksum_sha256: string;
  source: string;
  license: string;
  music_rights: string | null;
  validation_status: string;
  metadata: Record<string, unknown>;
  created_at: string;
}
export interface MediaJob {
  id: string;
  status: string;
  attempt_count: number;
  max_attempts: number;
  scheduled_at: string;
  last_error: { code?: string; message?: string } | null;
  correlation_id: string;
}
export interface MediaAgent {
  id: string;
  name: string;
  status: string;
  last_seen_at: string | null;
  capabilities: string[];
}

export interface SocialPolicy {
  publishing_enabled: boolean;
  allow_direct: boolean;
  allow_draft: boolean;
  require_manual_review: boolean;
  allow_image: boolean;
  allow_carousel: boolean;
  allow_story: boolean;
  allow_video: boolean;
  minimum_lead_minutes: number;
  metrics_enabled: boolean;
  metrics_interval_hours: number;
}

export interface SocialAccount {
  id: string;
  platform: 'instagram' | 'facebook' | 'tiktok';
  display_name: string;
  account_type: string;
  status: string;
  scopes: string[];
  app_review_status: string;
  development_publish_test: boolean;
  token_expires_at: string | null;
  capabilities: Record<string, string>;
  policy: SocialPolicy;
}

export interface SocialPublication {
  id: string;
  content_id: string;
  platform: SocialAccount['platform'];
  platform_account_id: string;
  caption: string;
  delivery_status: string;
  master_scheduled_at: string;
  dispatched_at: string | null;
  accepted_at: string | null;
  published_at: string | null;
  provider_post_id: string | null;
  remote_publish_id: string | null;
  last_error: {
    code?: string;
    retryable?: boolean;
    ambiguous?: boolean;
  } | null;
  publish_mode: 'DIRECT' | 'DRAFT' | 'MANUAL';
  next_metrics_sync_at: string | null;
  last_metrics_sync_at: string | null;
  metrics_sync_error: { code?: string } | null;
}

export interface SocialComment {
  provider: 'instagram' | 'facebook';
  provider_comment_id: string;
  body: string;
  provider_created_at: string | null;
  fetched_at: string;
}

const apiUrl = import.meta.env.VITE_API_URL ?? 'http://localhost:8787';

export class ApiRequestError extends Error {
  constructor(
    message: string,
    readonly code: string,
    readonly httpStatus: number | null,
    readonly stage: string | null = null,
    readonly requestId: string | null = null,
  ) {
    super(message);
    this.name = 'ApiRequestError';
  }
}

async function request<T>(
  path: string,
  session: Session,
  init?: RequestInit,
): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${apiUrl}${path}`, {
      ...init,
      headers: {
        Authorization: `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
        ...init?.headers,
      },
    });
  } catch {
    throw new ApiRequestError(
      'Não foi possível conectar à API. Verifique a conexão e tente novamente.',
      'API_NETWORK_ERROR',
      null,
    );
  }
  const body = (await response.json().catch(() => null)) as
    | (T & {
        error?: { code?: string; message?: string; stage?: string };
        requestId?: string;
      })
    | null;
  if (!response.ok) {
    const rawCode = body?.error?.code;
    const code =
      typeof rawCode === 'string' && /^[A-Z][A-Z0-9_]{0,63}$/.test(rawCode)
        ? rawCode
        : 'API_REQUEST_FAILED';
    const rawStage = body?.error?.stage;
    const stage =
      typeof rawStage === 'string' && /^[a-z_]{1,40}$/.test(rawStage)
        ? rawStage
        : null;
    const requestId =
      typeof body?.requestId === 'string' &&
      /^[0-9a-f-]{36}$/i.test(body.requestId)
        ? body.requestId
        : null;
    throw new ApiRequestError(
      friendlyApiError(
        code,
        path.includes('/instagram-readiness')
          ? undefined
          : body?.error?.message,
      ),
      code,
      response.status,
      stage,
      requestId,
    );
  }
  if (body === null)
    throw new ApiRequestError(
      'A API retornou uma resposta inesperada. Tente novamente.',
      'API_RESPONSE_INVALID',
      response.status,
    );
  return body;
}

export const api = {
  intelligence: {
    codexPermission: (session: Session) =>
      request<{ enabled: boolean }>('/v1/intelligence/codex-settings', session),
    setCodexPermission: (session: Session, enabled: boolean) =>
      request<{ enabled: boolean }>(
        '/v1/intelligence/codex-settings',
        session,
        {
          method: 'PATCH',
          body: JSON.stringify({ enabled }),
        },
      ),
    overview: (session: Session) =>
      request<IntelligenceData>('/v1/intelligence', session),
    analyze: (session: Session) =>
      request<{ runId: string; reused: boolean }>(
        '/v1/intelligence/analyze',
        session,
        { method: 'POST' },
      ),
    decide: (session: Session, id: string, approve: boolean, reason: string) =>
      request(
        '/v1/intelligence/findings/' + encodeURIComponent(id) + '/decision',
        session,
        { method: 'POST', body: JSON.stringify({ approve, reason }) },
      ),
    revert: (session: Session, id: string) =>
      request(
        '/v1/intelligence/adjustments/' + encodeURIComponent(id) + '/revert',
        session,
        { method: 'POST' },
      ),
    settings: (
      session: Session,
      settings: Partial<NonNullable<IntelligenceData['settings']>>,
    ) =>
      request('/v1/intelligence/settings', session, {
        method: 'PATCH',
        body: JSON.stringify(settings),
      }),
    history: (session: Session, publicationId: string) =>
      request<{
        history: Array<{
          hours: number;
          capturedAt: string | null;
          metrics: Record<string, number>;
          rates: Record<string, number>;
        }>;
      }>(
        '/v1/intelligence/history/' + encodeURIComponent(publicationId),
        session,
      ),
  },
  social: {
    accounts: (session: Session) =>
      request<{ accounts: SocialAccount[] }>('/v1/social/accounts', session),
    instagramReadiness: (session: Session, id: string, assetId: string) =>
      request<{
        requestId: string;
        accountId: string;
        instagramId: string;
        storedScopes: string[];
        storedTokenExpiresAt: string | null;
        tokenDecryptable: boolean;
        graph: {
          tokenValid: boolean;
          tokenType: string | null;
          expiresAt: string | null;
          grantedScopes: string[];
          requiredScopesGranted: boolean;
          pageId: string | null;
          pageName: string | null;
          linkedInstagramId: string | null;
          instagramUsername: string | null;
          instagramAccountType: string | null;
          professionalAccount: boolean;
          graphHttpStatus: number | null;
          providerCode: number | null;
          failedStage: 'debug_token' | 'page_lookup' | 'profile_lookup' | null;
        } | null;
        pageAccountId: string | null;
        pageStoredName: string | null;
        developmentPublishAllowed: boolean;
        appReviewStatus: string;
        editorialItemId: string | null;
        editorialStatus: string | null;
        contentId: string;
        assetId: string;
        media: {
          url: string;
          expiredUrl: string;
          expiresAt: string;
          expectedMime: string;
          expectedBytes: number;
          expectedSha256: string;
        } | null;
      }>(
        '/v1/social/accounts/' +
          encodeURIComponent(id) +
          '/instagram-readiness?assetId=' +
          encodeURIComponent(assetId),
        session,
      ),
    publications: (session: Session) =>
      request<{ publications: SocialPublication[] }>(
        '/v1/social/publications',
        session,
      ),
    connect: (session: Session, platform: SocialAccount['platform']) =>
      request<{ authorizationUrl: string }>(
        '/v1/social/oauth/' + platform + '/start',
        session,
        { method: 'POST', credentials: 'include' },
      ),
    savePolicy: (session: Session, id: string, policy: Partial<SocialPolicy>) =>
      request<{ policy: SocialPolicy }>(
        '/v1/social/accounts/' + encodeURIComponent(id) + '/policy',
        session,
        { method: 'PATCH', body: JSON.stringify(policy) },
      ),
    creatorInfo: (session: Session, id: string) =>
      request<{
        creatorInfo: {
          privacy_level_options: string[];
          comment_disabled: boolean;
          duet_disabled: boolean;
          stitch_disabled: boolean;
          max_video_post_duration_sec: number;
        };
      }>(
        '/v1/social/accounts/' + encodeURIComponent(id) + '/creator-info',
        session,
      ),
    schedule: (session: Session, body: object) =>
      request<{ publicationId: string; status: string }>(
        '/v1/social/publications',
        session,
        { method: 'POST', body: JSON.stringify(body) },
      ),
    retry: (session: Session, id: string) =>
      request(
        '/v1/social/publications/' + encodeURIComponent(id) + '/retry',
        session,
        { method: 'POST' },
      ),
    disconnect: (session: Session, id: string) =>
      request(
        '/v1/social/accounts/' + encodeURIComponent(id) + '/disconnect',
        session,
        { method: 'POST' },
      ),
    metrics: (session: Session, id: string) =>
      request<{
        metrics: Array<{
          metric_name: string;
          provider_value: number;
          captured_at: string;
        }>;
      }>(
        '/v1/social/publications/' + encodeURIComponent(id) + '/metrics',
        session,
      ),
    comments: (session: Session, id: string) =>
      request<{ comments: SocialComment[] }>(
        '/v1/social/publications/' + encodeURIComponent(id) + '/comments',
        session,
      ),
    syncComments: (session: Session, id: string) =>
      request<{ count: number }>(
        '/v1/social/publications/' + encodeURIComponent(id) + '/comments/sync',
        session,
        { method: 'POST' },
      ),
  },
  dashboard: (session: Session) =>
    request<DashboardData>('/v1/dashboard', session),
  setPaused: (session: Session, automationPaused: boolean) =>
    request<{ automation: DashboardData['automation'] }>(
      '/v1/automation-settings',
      session,
      { method: 'PATCH', body: JSON.stringify({ automationPaused }) },
    ),
  setMode: (session: Session, operationMode: 'SUPERVISED' | 'AUTONOMOUS') =>
    request<{ automation: DashboardData['automation'] }>(
      '/v1/automation-settings',
      session,
      { method: 'PATCH', body: JSON.stringify({ operationMode }) },
    ),
  runCycle: (session: Session) =>
    request<{ id: string; status: string }>('/v1/cycles', session, {
      method: 'POST',
    }),
  editorial: {
    bootstrap: (session: Session) =>
      request<EditorialBootstrap>('/v1/editorial/bootstrap', session),
    opportunities: (session: Session) =>
      request<{ source: string; opportunities: EditorialIdea[] }>(
        '/v1/editorial/opportunities',
        session,
      ),
    research: (session: Session) =>
      request<{ source: string; created: number }>(
        '/v1/editorial/research',
        session,
        { method: 'POST' },
      ),
    memory: (session: Session) =>
      request<{
        windowDays: number;
        items: EditorialItem[];
        contents: Array<{ id: string; title: string; status: string }>;
        manualCandidates: Array<{
          id: string;
          content_id: string;
          mime_type: string;
          width: number;
          height: number;
        }>;
        socialMetricsAvailable: boolean;
      }>('/v1/editorial/memory', session),
    saveDna: (session: Session, data: Partial<EditorialDna>) =>
      request('/v1/editorial/dna', session, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    saveSettings: (session: Session, data: Partial<EditorialSettings>) =>
      request('/v1/editorial/settings', session, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    create: (
      session: Session,
      resource: 'pillars' | 'series' | 'references' | 'ideas',
      data: object,
    ) =>
      request('/v1/editorial/resources/' + resource, session, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    update: (
      session: Session,
      resource: 'pillars' | 'series' | 'references' | 'ideas' | 'rules',
      id: string,
      data: object,
    ) =>
      request('/v1/editorial/resources/' + resource + '/' + id, session, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    plan: (session: Session, planDate: string) =>
      request<{ plans: EditorialPlan[]; items: EditorialItem[] }>(
        '/v1/editorial/plans',
        session,
        { method: 'POST', body: JSON.stringify({ planDate }) },
      ),
    day: (session: Session, planDate: string) =>
      request<{ plans: EditorialPlan[]; items: EditorialItem[] }>(
        '/v1/editorial/plans?date=' + encodeURIComponent(planDate),
        session,
      ),
    calendar: (session: Session, from: string, to: string) =>
      request<{ items: EditorialItem[] }>(
        '/v1/editorial/calendar?from=' +
          encodeURIComponent(from) +
          '&to=' +
          encodeURIComponent(to),
        session,
      ),
    manualReview: (
      session: Session,
      contentId: string,
      assetId: string,
      planDate: string,
      masterTime: string,
    ) =>
      request<{ itemId: string; status: string }>(
        '/v1/editorial/manual-reviews',
        session,
        {
          method: 'POST',
          body: JSON.stringify({ contentId, assetId, planDate, masterTime }),
        },
      ),
    updateItem: (session: Session, id: string, data: object) =>
      request('/v1/editorial/items/' + id, session, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
  },
  media: {
    templates: (session: Session) =>
      request<{ templates: MediaTemplate[] }>('/v1/media/templates', session),
    createTemplate: (session: Session, data: object) =>
      request('/v1/media/templates', session, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    setTemplateActive: (session: Session, id: string, active: boolean) =>
      request('/v1/media/templates/' + encodeURIComponent(id), session, {
        method: 'PATCH',
        body: JSON.stringify({ active }),
      }),
    specs: (session: Session) =>
      request<{ specs: MediaSpec[] }>('/v1/media/specs', session),
    jobs: (session: Session) =>
      request<{ jobs: MediaJob[] }>('/v1/media/jobs', session),
    assets: (session: Session) =>
      request<{ assets: MediaAsset[] }>('/v1/media/assets', session),
    agents: (session: Session) =>
      request<{ agents: MediaAgent[] }>('/v1/media/agents', session),
    createAgent: (session: Session, name: string) =>
      request<{ agentId: string }>('/v1/media/agents', session, {
        method: 'POST',
        body: JSON.stringify({ name }),
      }),
    createSpec: (session: Session, data: object) =>
      request<{ specId: string }>('/v1/media/specs', session, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    retry: (session: Session, id: string) =>
      request('/v1/media/specs/' + encodeURIComponent(id) + '/retry', session, {
        method: 'POST',
      }),
    uploadOriginal: async (
      session: Session,
      file: File,
      license: string,
      source: string,
    ) => {
      const response = await fetch(apiUrl + '/v1/media/originals', {
        method: 'POST',
        headers: {
          Authorization: 'Bearer ' + session.access_token,
          'Content-Type': file.type,
          'X-File-Name': encodeURIComponent(file.name),
          'X-Media-License': encodeURIComponent(license),
          'X-Media-Source': encodeURIComponent(source),
        },
        body: file,
      });
      const data = (await response.json()) as {
        asset?: MediaAsset;
        error?: { message?: string };
      };
      if (!response.ok)
        throw new Error(data.error?.message ?? 'Upload rejeitado.');
      return data.asset!;
    },
    assetBlob: async (session: Session, id: string) => {
      const response = await fetch(
        apiUrl + '/v1/media/assets/' + encodeURIComponent(id) + '/file',
        {
          headers: { Authorization: 'Bearer ' + session.access_token },
        },
      );
      if (!response.ok) throw new Error('Preview indisponível.');
      return response.blob();
    },
  },
};
